import { getQatarDayRange, utcToQatarDateStr } from '@/utils/timezone';

const SMART_GAP_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
const SCREENSHOT_BUFFER_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Calculate duration in hours between two timestamps.
 * If endTime is null (ongoing session), uses current time.
 */
export function calculateSessionHours(
  startTime: string | Date,
  endTime: string | Date | null
): number {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const diffMs = end.getTime() - start.getTime();

  if (diffMs <= 0) return 0;

  return diffMs / (1000 * 60 * 60);
}

/**
 * Calculate duration in seconds between two timestamps.
 * If endTime is null (ongoing session), uses current time.
 */
export function calculateSessionSeconds(
  startTime: string | Date,
  endTime: string | Date | null
): number {
  return Math.round(calculateSessionHours(startTime, endTime) * 3600);
}

/**
 * Format seconds into human-readable duration string.
 * Examples: "2h 15m", "45m", "< 1m"
 */
export function formatDurationFromSeconds(totalSeconds: number): string {
  if (totalSeconds < 60) return '< 1m';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Format hours into human-readable duration string.
 */
export function formatDurationFromHours(hours: number): string {
  return formatDurationFromSeconds(hours * 3600);
}

/**
 * Calculate effective hours for a session.
 * Uses raw duration (end - start), no cap.
 * Only deducted_seconds (from screenshot deletions) are subtracted.
 * Idle time is tracked as INFO only and NOT subtracted.
 */
export function calculateEffectiveHours(
  startTime: string | Date,
  endTime: string | Date | null,
  _idleSeconds: number = 0,
  deductedSeconds: number = 0
): number {
  const rawHours = calculateSessionHours(startTime, endTime);
  const deductedHours = deductedSeconds / 3600;
  return Math.max(0, rawHours - deductedHours);
}

/**
 * Get app/URL log duration from database fields.
 * Prefers duration_seconds (trigger-computed), falls back to started_at/ended_at calc.
 * Never falls back to hardcoded 60s.
 */
export function getLogDuration(log: {
  duration_seconds?: number | null;
  started_at?: string | null;
  ended_at?: string | null;
}): number {
  if (log.duration_seconds && log.duration_seconds > 0) {
    return log.duration_seconds;
  }
  if (log.started_at && log.ended_at) {
    const start = new Date(log.started_at);
    const end = new Date(log.ended_at);
    const seconds = (end.getTime() - start.getTime()) / 1000;
    return Math.max(0, seconds);
  }
  return 0;
}

/**
 * Allocate an app/URL slice's recorded duration to its overlap with a report.
 * Preserve the stored duration contract; an open slice without a recorded
 * duration contributes zero rather than inventing elapsed time until now.
 */
export function getLogDurationInRange(
  log: Parameters<typeof getLogDuration>[0],
  rangeStartMs: number,
  rangeEndMs: number
): number {
  const durationSeconds = getLogDuration(log);
  const startMs = log.started_at ? new Date(log.started_at).getTime() : NaN;
  const endMs = log.ended_at ? new Date(log.ended_at).getTime() : startMs + durationSeconds * 1000;
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0
    || !Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  const overlapMs = Math.max(0, Math.min(endMs, rangeEndMs) - Math.max(startMs, rangeStartMs));
  return durationSeconds * (overlapMs / (endMs - startMs));
}

/**
 * Calculate SMART session duration in seconds, capping sessions where the last screenshot
 * is far before the session end_time (e.g. session stayed open overnight).
 *
 * If lastScreenshotTime exists and (endTime - lastScreenshotTime) > 30 minutes:
 *   return (lastScreenshotTime + 5min - startTime)
 * Otherwise: return normal (endTime - startTime)
 */
export function calculateSmartSessionSeconds(
  startTime: string | Date,
  endTime: string | Date | null,
  lastScreenshotTime?: string | Date | null
): number {
  const startMs = new Date(startTime).getTime();
  const endMs = endTime ? new Date(endTime).getTime() : Date.now();

  if (endMs <= startMs) return 0;

  // If last screenshot exists and gap is > 30 minutes, use screenshot-based end
  if (lastScreenshotTime) {
    const lastSsMs = new Date(lastScreenshotTime).getTime();
    if (lastSsMs >= startMs && (endMs - lastSsMs) > SMART_GAP_THRESHOLD_MS) {
      const smartEndMs = lastSsMs + SCREENSHOT_BUFFER_MS;
      const smartDurationMs = smartEndMs - startMs;
      return Math.max(0, Math.round(smartDurationMs / 1000));
    }
  }

  // Normal calculation — raw duration
  const durationMs = endMs - startMs;
  return Math.max(0, Math.round(durationMs / 1000));
}

/**
 * Compute smart end time (in ms) for a session interval, capping with last screenshot.
 * Used by interval-merging calculations across report pages.
 */
export function getSmartEndMs(
  startMs: number,
  endMs: number,
  lastScreenshotMs?: number
): number {
  if (lastScreenshotMs && lastScreenshotMs >= startMs) {
    const gapMs = endMs - lastScreenshotMs;
    if (gapMs > SMART_GAP_THRESHOLD_MS) {
      return Math.max(startMs, lastScreenshotMs + SCREENSHOT_BUFFER_MS);
    }
  }
  return Math.max(startMs, endMs);
}

// --- Multi-device time merging ---

export interface TimeInterval {
  startMs: number;
  endMs: number;
}

export interface DatedTimeInterval extends TimeInterval {
  date: string;
}

export interface TimeLogForMerge {
  id?: string;
  start_time: string;
  end_time: string | null;
  idle_seconds?: number | null;
  deducted_seconds?: number | null;
  status?: string | null;
  user_id: string;
}

export interface ReportableTimeInterval extends TimeInterval {
  userId: string;
  logId?: string;
}

export interface ReportableIntervalOptions {
  rangeStartMs?: number;
  rangeEndMs?: number;
  nowMs?: number;
  screenshotsBySession?: Map<string, number>;
}

/**
 * Return the only open session that should count for each user.
 * Older unclosed rows are stale data and must not inflate dashboard/report totals.
 */
export function getLatestOpenTimeLogIdsByUser(logs: TimeLogForMerge[]): Set<string> {
  const openIds = new Set<string>();
  const seenUsers = new Set<string>();

  [...logs]
    .filter(log => !log.end_time && !!log.start_time)
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
    .forEach(log => {
      const isActive = !log.status || log.status === 'active';
      if (!isActive || !log.id || seenUsers.has(log.user_id)) return;
      seenUsers.add(log.user_id);
      openIds.add(log.id);
    });

  return openIds;
}

/**
 * Normalize time_logs into reportable intervals using the same rules everywhere:
 * - completed sessions use their stored end_time
 * - only the latest active open session per user runs until now
 * - stale unclosed sessions are ignored
 * - screenshot deletion deductions are subtracted from the session end
 * - optional ranges clamp sessions that overlap a report window
 */
export function getReportableTimeLogIntervals(
  logs: TimeLogForMerge[],
  options: ReportableIntervalOptions = {}
): ReportableTimeInterval[] {
  const now = options.nowMs ?? Date.now();
  const activeOpenIds = getLatestOpenTimeLogIdsByUser(logs);
  const latestOpenByUser = new Map<string, TimeLogForMerge>();
  [...logs]
    .filter(log => !log.end_time && !!log.start_time && (!log.status || log.status === 'active'))
    .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
    .forEach(log => {
      if (!latestOpenByUser.has(log.user_id)) latestOpenByUser.set(log.user_id, log);
    });
  const intervals: ReportableTimeInterval[] = [];

  for (const log of logs) {
    if (!log.start_time) continue;
    const rawStartMs = new Date(log.start_time).getTime();
    if (!Number.isFinite(rawStartMs)) continue;

    let rawEndMs: number;
    if (log.end_time) {
      rawEndMs = new Date(log.end_time).getTime();
    } else if ((log.id && activeOpenIds.has(log.id)) || latestOpenByUser.get(log.user_id) === log) {
      rawEndMs = now;
    } else {
      continue;
    }

    if (!Number.isFinite(rawEndMs) || rawEndMs <= rawStartMs) continue;

    const lastSsMs = log.id ? options.screenshotsBySession?.get(log.id) : undefined;
    rawEndMs = getSmartEndMs(rawStartMs, rawEndMs, lastSsMs);
    rawEndMs = Math.max(rawStartMs, rawEndMs - ((log.deducted_seconds || 0) * 1000));

    const startMs = options.rangeStartMs != null
      ? Math.max(rawStartMs, options.rangeStartMs)
      : rawStartMs;
    const endMs = options.rangeEndMs != null
      ? Math.min(rawEndMs, options.rangeEndMs)
      : rawEndMs;

    if (endMs <= startMs) continue;

    intervals.push({
      startMs,
      endMs,
      userId: log.user_id,
      logId: log.id,
    });
  }

  return intervals;
}

export interface ScreenshotTimeEvidence {
  user_id: string | null;
  time_log_id: string | null;
  captured_at: string;
  image_deleted_at?: string | null;
}

/**
 * Bind duration evidence to its canonical, reportable session and owner.
 * Image retention leaves this metadata intact; it does not deduct worked time.
 * Direct canonical evidence always takes priority. Legacy null-session rows
 * may cap a session only when its owner/raw timestamps identify exactly one
 * reportable session and that session has no direct evidence.
 */
export function getLastScreenshotByTimeLogId(
  logs: TimeLogForMerge[],
  screenshots: ScreenshotTimeEvidence[],
  options: Omit<ReportableIntervalOptions, 'screenshotsBySession'> = {}
): Map<string, number> {
  const nowMs = options.nowMs ?? Date.now();
  const reportableIds = new Set(getReportableTimeLogIntervals(logs, { ...options, nowMs })
    .map(interval => interval.logId));
  const logsById = new Map(logs.filter(log => log.id && reportableIds.has(log.id))
    .map(log => [log.id, log]));
  const result = new Map<string, number>();

  for (const screenshot of screenshots) {
    if (!screenshot.time_log_id) continue;
    const log = logsById.get(screenshot.time_log_id);
    if (!log || screenshot.user_id !== log.user_id) continue;
    const capturedMs = new Date(screenshot.captured_at).getTime();
    const startMs = new Date(log.start_time).getTime();
    const endMs = log.end_time ? new Date(log.end_time).getTime() : nowMs;
    if (!Number.isFinite(capturedMs) || capturedMs < startMs || capturedMs > endMs) continue;
    result.set(screenshot.time_log_id, Math.max(result.get(screenshot.time_log_id) ?? -Infinity, capturedMs));
  }

  const directIds = new Set(result.keys());
  for (const screenshot of screenshots) {
    if (screenshot.time_log_id || !screenshot.user_id) continue;
    const capturedMs = new Date(screenshot.captured_at).getTime();
    if (!Number.isFinite(capturedMs)) continue;
    const matches = [...logsById.values()].filter(log => screenshot.user_id === log.user_id
      && capturedMs >= new Date(log.start_time).getTime()
      && capturedMs <= (log.end_time ? new Date(log.end_time).getTime() : nowMs));
    if (matches.length !== 1 || directIds.has(matches[0].id!)) continue;
    const id = matches[0].id!;
    result.set(id, Math.max(result.get(id) ?? -Infinity, capturedMs));
  }
  return result;
}

/**
 * Merge overlapping time intervals into non-overlapping ranges.
 * Input must be sorted by startMs. Returns merged, non-overlapping intervals.
 */
export function mergeTimeIntervals(intervals: TimeInterval[]): TimeInterval[] {
  if (intervals.length === 0) return [];

  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs);
  const merged: TimeInterval[] = [{ ...sorted[0] }];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (current.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, current.endMs);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
}

/**
 * Split an interval into Qatar-local calendar days and clamp it to a report range.
 * This keeps monthly/admin reports exact when a session crosses midnight.
 */
export function splitIntervalByQatarDay(
  startMs: number,
  endMs: number,
  rangeStartMs: number,
  rangeEndMs: number
): DatedTimeInterval[] {
  let cursor = Math.max(startMs, rangeStartMs);
  const finalEnd = Math.min(endMs, rangeEndMs);

  if (finalEnd <= cursor) return [];

  const parts: DatedTimeInterval[] = [];

  while (cursor < finalEnd) {
    const date = utcToQatarDateStr(new Date(cursor).toISOString());
    const { end } = getQatarDayRange(date);
    const dayEndMs = new Date(end).getTime();
    const partEnd = Math.min(finalEnd, dayEndMs);

    if (partEnd > cursor) {
      parts.push({ date, startMs: cursor, endMs: partEnd });
    }

    cursor = partEnd;
  }

  return parts;
}

/**
 * Calculate total hours for a user from multiple (possibly overlapping) time logs.
 * Merges overlapping intervals so multi-device sessions are not double-counted.
 *
 * Only deducted_seconds (from screenshot deletions) are subtracted.
 * Idle time is tracked as INFO only and NOT subtracted from hours.
 */
export function calculateMergedEffectiveHours(
  logs: TimeLogForMerge[],
  screenshotsBySession?: Map<string, number>,
  options: Omit<ReportableIntervalOptions, 'screenshotsBySession'> = {}
): number {
  if (logs.length === 0) return 0;

  const intervals = getReportableTimeLogIntervals(logs, { ...options, screenshotsBySession });

  const merged = mergeTimeIntervals(intervals);

  let totalMs = 0;
  for (const interval of merged) {
    totalMs += interval.endMs - interval.startMs;
  }

  return Math.max(0, totalMs / (1000 * 60 * 60));
}

/**
 * Group time logs by user_id and calculate merged effective hours per user.
 * Returns a map of user_id -> effective hours.
 */
export function calculateMergedHoursByUser(
  logs: TimeLogForMerge[],
  options: Omit<ReportableIntervalOptions, 'screenshotsBySession'> = {}
): Map<string, number> {
  const byUser = new Map<string, TimeLogForMerge[]>();

  for (const log of logs) {
    const userId = log.user_id;
    if (!byUser.has(userId)) byUser.set(userId, []);
    byUser.get(userId)!.push(log);
  }

  const result = new Map<string, number>();
  for (const [userId, userLogs] of byUser) {
    result.set(userId, calculateMergedEffectiveHours(userLogs, undefined, options));
  }

  return result;
}

/**
 * Calculate total merged seconds from intervals within a date range.
 * Used by live monitoring to compute worked seconds with overlap protection.
 */
export function calculateMergedWorkedSeconds(
  timeLogs: Array<{ start_time?: string; started_at?: string; end_time?: string; ended_at?: string; id?: string }>,
  fromISO: string
): number {
  const from = new Date(fromISO).getTime();
  const now = Date.now();

  const intervals: TimeInterval[] = [];

  const sorted = [...timeLogs].sort((a, b) => {
    const aStart = new Date(a.start_time || a.started_at || '').getTime();
    const bStart = new Date(b.start_time || b.started_at || '').getTime();
    return bStart - aStart;
  });
  const mostRecentId = sorted[0]?.id;

  for (const t of timeLogs) {
    const start = new Date(t.start_time || t.started_at || '').getTime();
    const hasEndTime = !!(t.end_time || t.ended_at);

    let end: number;
    if (hasEndTime) {
      end = new Date(t.end_time || t.ended_at || '').getTime();
    } else if (t.id === mostRecentId) {
      end = now;
    } else {
      continue;
    }

    const overlapStart = Math.max(start, from);
    const overlapEnd = Math.max(overlapStart, end);

    if (overlapEnd > overlapStart) {
      intervals.push({ startMs: overlapStart, endMs: overlapEnd });
    }
  }

  const merged = mergeTimeIntervals(intervals);

  let total = 0;
  for (const interval of merged) {
    const seconds = Math.floor((interval.endMs - interval.startMs) / 1000);
    total += seconds;
  }
  return total;
}
